import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getBillingStatus, subscribeToPlan, type BillingStatus } from '../api';

interface PlanDef {
  slug: string;
  label: string;
  price: string;
  priceNote: string;
  maxWidgets: string;
  brandingRemoval: boolean;
  analytics: boolean;
  abTesting: boolean;
  customCss: boolean;
  impressions: string;
  prioritySupport: boolean;
}

const PLANS: PlanDef[] = [
  {
    slug: 'starter',
    label: 'Starter',
    price: 'Free',
    priceNote: 'Always free',
    maxWidgets: '1 widget',
    brandingRemoval: false,
    analytics: false,
    abTesting: false,
    customCss: false,
    impressions: '1,000/mo',
    prioritySupport: false,
  },
  {
    slug: 'growth',
    label: 'Growth',
    price: '$14.99',
    priceNote: 'per month',
    maxWidgets: 'Unlimited',
    brandingRemoval: true,
    analytics: true,
    abTesting: false,
    customCss: false,
    impressions: 'Unlimited',
    prioritySupport: false,
  },
  {
    slug: 'pro',
    label: 'Pro',
    price: '$24.99',
    priceNote: 'per month',
    maxWidgets: 'Unlimited',
    brandingRemoval: true,
    analytics: true,
    abTesting: true,
    customCss: true,
    impressions: 'Unlimited',
    prioritySupport: true,
  },
];

function featureCheck(val: boolean) {
  return val
    ? html`<s-text aria-label="Included"><span style="color: var(--p-color-text-success, #008060);">&#10003;</span></s-text>`
    : html`<s-text tone="subdued" aria-label="Not included">&#8212;</s-text>`;
}

@customElement('hype-billing-page')
export class BillingPage extends LitElement {
  override createRenderRoot() { return this; }

  @state() private _loading = true;
  @state() private _error = '';
  @state() private _billing: BillingStatus | null = null;
  @state() private _subscribing: string | null = null; // plan slug being subscribed to

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadBilling();
  }

  private async _loadBilling(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      this._billing = await getBillingStatus();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Failed to load billing status.';
    } finally {
      this._loading = false;
    }
  }

  private async _handleSubscribe(planSlug: string): Promise<void> {
    if (this._subscribing) return;
    this._subscribing = planSlug;
    try {
      const { confirmation_url } = await subscribeToPlan(planSlug);
      if (confirmation_url) {
        window.open(confirmation_url, '_top');
      } else {
        // Free plan downgrade — applied immediately, reload billing status
        this._billing = await getBillingStatus();
        this._subscribing = null;
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Failed to start subscription. Please try again.';
      this._subscribing = null;
    }
  }

  private _currentPlan(): string {
    return this._billing?.plan ?? '';
  }

  private _planIndex(slug: string): number {
    return PLANS.findIndex(p => p.slug === slug);
  }

  private _renderPlanAction(plan: PlanDef) {
    const current = this._currentPlan();
    const currentIdx = this._planIndex(current);
    const planIdx = this._planIndex(plan.slug);
    const isRecommended = plan.slug === 'growth';

    if (plan.slug === current) {
      return html`
        <s-button variant="secondary" disabled fullWidth aria-label="Your current plan">
          Current plan
        </s-button>
      `;
    }

    if (plan.slug === 'starter') {
      // Downgrade to free — same subscribe call, backend handles it
      return html`
        <s-button
          variant="secondary"
          fullWidth
          @click=${() => void this._handleSubscribe('starter')}
          ?disabled=${!!this._subscribing}
          aria-label="Downgrade to Starter (free)"
        >
          ${this._subscribing === 'starter' ? html`<s-spinner size="small"></s-spinner> Downgrading…` : 'Downgrade to Free'}
        </s-button>
      `;
    }

    const isUpgrade = planIdx > currentIdx;
    const label = isUpgrade ? `Upgrade to ${plan.label}` : `Switch to ${plan.label}`;
    return html`
      <s-button
        variant=${isRecommended || isUpgrade ? 'primary' : 'secondary'}
        fullWidth
        @click=${() => void this._handleSubscribe(plan.slug)}
        ?disabled=${!!this._subscribing}
        aria-label="${label}"
      >
        ${this._subscribing === plan.slug
          ? html`<s-spinner size="small"></s-spinner> Processing…`
          : label}
      </s-button>
    `;
  }

  private _renderFeatureRow(label: string, value: string | boolean, badge?: string) {
    return html`
      <s-stack direction="inline" gap="small" alignItems="center" role="listitem">
        <s-text variant="bodySm" style="flex:1">${label}${badge ? html` <s-badge tone="info">${badge}</s-badge>` : ''}</s-text>
        ${typeof value === 'boolean'
          ? featureCheck(value)
          : html`<s-text variant="bodySm" fontWeight="semibold">${value}</s-text>`}
      </s-stack>
    `;
  }

  private _renderPlanCard(plan: PlanDef) {
    const current = this._currentPlan();
    const isCurrent = plan.slug === current;
    const isRecommended = plan.slug === 'growth';

    return html`
      <div class="billing-card ${isRecommended ? 'billing-card--recommended' : ''} ${isCurrent ? 'billing-card--current' : ''}">
        <s-box
          padding="large"
          background="base"
          borderWidth="base"
          borderColor=${isRecommended ? 'info' : 'border'}
          borderRadius="base"
          aria-label="Plan: ${plan.label}"
        >
          <s-stack gap="base">

            <!-- Badges row -->
            <s-stack direction="inline" gap="small" alignItems="center">
              ${isRecommended ? html`<s-badge tone="success">Most Popular</s-badge>` : ''}
              ${isCurrent ? html`<s-badge tone="info">Current plan</s-badge>` : ''}
              ${!isRecommended && !isCurrent ? html`<span style="height: 24px; display: block;"></span>` : ''}
            </s-stack>

            <!-- Plan name -->
            <s-text variant="headingMd">${plan.label}</s-text>

            <!-- Price -->
            <s-stack gap="small-300">
              <s-stack direction="inline" gap="small-200" alignItems="baseline">
                <s-text variant="headingXl" aria-label="Price: ${plan.price}">${plan.price}</s-text>
                <s-text variant="bodySm" tone="subdued">${plan.priceNote}</s-text>
              </s-stack>
            </s-stack>

            <!-- CTA -->
            ${this._renderPlanAction(plan)}

            <!-- Divider -->
            <hr style="border: none; border-top: 1px solid var(--p-color-border-secondary, #e1e3e5); margin: 0;" />

            <!-- Feature list -->
            <s-stack gap="small" role="list" aria-label="${plan.label} plan features">
              ${this._renderFeatureRow('Widgets', plan.maxWidgets)}
              ${this._renderFeatureRow('Impressions', plan.impressions)}
              ${this._renderFeatureRow('Remove branding', plan.brandingRemoval)}
              ${this._renderFeatureRow('Analytics', plan.analytics)}
              ${this._renderFeatureRow('A/B testing', plan.abTesting, 'Soon')}
              ${this._renderFeatureRow('Custom CSS', plan.customCss)}
              ${this._renderFeatureRow('Priority support', plan.prioritySupport)}
            </s-stack>

          </s-stack>
        </s-box>
      </div>
    `;
  }

  override render() {
    return html`
      <style>
        /* Recommended card emphasis */
        hype-billing-page .billing-card--recommended s-box {
          border-color: var(--p-color-border-info, #006fbb) !important;
          box-shadow: 0 0 0 1px var(--p-color-border-info, #006fbb);
        }

        /* Plan card grid — responsive */
        hype-billing-page .billing-plan-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--p-space-base, 16px);
          align-items: start;
        }

        @media (max-width: 768px) {
          hype-billing-page .billing-plan-grid {
            grid-template-columns: 1fr;
            max-width: 420px;
          }
        }

        /* Subtle lift on recommended card */
        hype-billing-page .billing-card--recommended {
          position: relative;
          z-index: 1;
        }

        /* Full-width buttons inside cards */
        hype-billing-page .billing-card s-button {
          width: 100%;
        }
      </style>
      <s-page heading="Billing">

        ${this._loading ? html`
          <s-section heading="Loading">
            <s-stack direction="inline" gap="base" alignItems="center">
              <s-spinner></s-spinner>
              <s-text tone="subdued">Loading billing status…</s-text>
            </s-stack>
          </s-section>
        ` : this._error ? html`
          <s-section heading="Error">
            <s-banner tone="critical">
              <strong>Failed to load billing status</strong><br />${this._error}
              <div style="margin-top:0.75rem">
                <s-button @click=${() => void this._loadBilling()}>Retry</s-button>
              </div>
            </s-banner>
          </s-section>
        ` : html`

          <s-section heading="Choose your plan">
            <s-text tone="subdued">
              All plans include 5 widget types with live preview. Upgrade or downgrade anytime — changes take effect immediately.
            </s-text>
            <div class="billing-plan-grid">
              ${PLANS.map(plan => this._renderPlanCard(plan))}
            </div>
          </s-section>

        `}

      </s-page>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hype-billing-page': BillingPage;
  }
}
